/**
 * Buffer-series grouping "brain" for the /products catalog.
 *
 * Pure grouping logic — no React, no live-data fetches. Buffer funds are issued
 * as a *family* of monthly "outcome period" variants (a May fund, a June fund,
 * …). On the catalog we collapse each family into ONE card with a month
 * dropdown instead of showing every monthly fund as its own card.
 *
 * The family key is DERIVED from `bufferConfig`: normalized reference ticker +
 * buffer level (e.g. "SPY|15"), the pair that defines the product. A newly
 * added month (July, August, …) joins its family's card + dropdown
 * automatically, with no per-fund tagging. `Fund.seriesSlug` is deliberately
 * NOT read here — it's hand-entered per fund and drifted in practice (the
 * July 2026 funds shipped with their own id in it, which split them out of
 * their families' dropdowns). Funds missing a reference ticker or a parseable
 * buffer level key by their own `id` and render as single-fund cards until
 * their config is completed.
 */
import type { Fund } from "@/data/funds";

/**
 * Calendar display order for a buffer family's months: the outcome year starts
 * in May, so May → Apr (with Jan–Apr treated as the *following* calendar year).
 * Keys are lowercase 3-char month prefixes (matches `bufferMonthKey`).
 */
export const BUFFER_MONTH_ORDER = [
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
  "jan",
  "feb",
  "mar",
  "apr",
] as const;

/**
 * Normalize a `bufferConfig.seriesMonth` value ("May", "June", "June 2026", …)
 * to its lowercase 3-char key ("may", "jun"). Returns "" for null/blank so the
 * caller can treat it as "unknown" and sort it last.
 */
export function bufferMonthKey(seriesMonth: string | null | undefined): string {
  return (seriesMonth ?? "").trim().slice(0, 3).toLowerCase();
}

/** Calendar sort index for a fund within its family; unknown months sort last. */
function bufferMonthIndex(fund: Fund): number {
  const idx = BUFFER_MONTH_ORDER.indexOf(
    bufferMonthKey(fund.bufferConfig?.seriesMonth) as (typeof BUFFER_MONTH_ORDER)[number],
  );
  return idx === -1 ? BUFFER_MONTH_ORDER.length : idx;
}

/**
 * Derived family key for a buffer fund: `"<REF>|<level>"` — reference ticker
 * uppercased, buffer level parsed numerically so "15", "15.00" and "15%" all
 * produce the same key. Falls back to the fund's own `id` when either half is
 * missing/unparseable, so an incomplete fund still renders (alone) rather than
 * mis-grouping. parseFloat (not Number) tolerates an admin-entered "15%",
 * matching formatBufferLevel in buffer-series-card.tsx.
 */
export function bufferFamilyKey(fund: Fund): string {
  const ref = (fund.bufferConfig?.referenceTicker ?? "").trim().toUpperCase();
  const level = Number.parseFloat(fund.bufferConfig?.bufferLevel ?? "");
  if (ref === "" || !Number.isFinite(level)) return fund.id;
  return `${ref}|${level}`;
}

/** One buffer family: the derived grouping key plus its monthly funds (always ≥ 1). */
export interface BufferSeriesGroup {
  familyKey: string;
  funds: [Fund, ...Fund[]];
}

/**
 * Group buffer funds into one entry per family.
 *
 * - Bucket key = `bufferFamilyKey(fund)` (reference ticker + buffer level,
 *   with fund-id fallback so an incomplete buffer fund still renders as its
 *   own one-fund "series").
 * - SERIES ORDER = order of first encounter in the input. The caller passes
 *   AUM-descending-sorted buffer funds, so families end up ordered by their
 *   highest-AUM month — the input order is preserved verbatim.
 * - WITHIN each family, funds sort by calendar month (May → Apr) via
 *   `bufferConfig.seriesMonth`; months that can't be parsed sort last.
 *
 * Nothing is omitted — every input fund lands in exactly one group.
 */
export function groupBufferBySeries(funds: Fund[]): BufferSeriesGroup[] {
  const order: string[] = [];
  const byKey = new Map<string, Fund[]>();

  for (const fund of funds) {
    const key = bufferFamilyKey(fund);
    const existing = byKey.get(key);
    if (existing) {
      existing.push(fund);
    } else {
      byKey.set(key, [fund]);
      order.push(key);
    }
  }

  return order.map((key) => {
    // Non-null: every key in `order` was inserted into `byKey` with ≥ 1 fund.
    const groupFunds = byKey.get(key) as [Fund, ...Fund[]];
    groupFunds.sort((a, b) => bufferMonthIndex(a) - bufferMonthIndex(b));
    return { familyKey: key, funds: groupFunds };
  });
}
